// xads — a thin, safe wrapper over the X (Twitter) Universal Website Tag, mirroring
// lib/analytics.ts. Every call no-ops when the pixel isn't configured
// (no NEXT_PUBLIC_X_PIXEL_ID), on the server, or before uwt.js has loaded — so an
// ad conversion can never break a user flow. The base tag itself is mounted once in
// components/XPixel.tsx; here we only *fire* against it.
//
// PRIVACY: the X pixel is the one thing in this stack that sets cross-site
// ADVERTISING cookies — unlike GA (consent-mode, cookieless) and PostHog
// (localStorage). It is therefore key-gated and OFF until the env var is set, and
// /privacy must disclose it before it goes live. We never pass email_address or any
// other identifier; conversions stay anonymous and count-only.

/** Conversion event IDs from the X Events Manager. One per tracked action. */
export const X_EVENTS = {
  /** account created — the primary conversion the boosted posts optimise toward. */
  signup: "tw-rdeo6-re3fi",
} as const;

type Twq = (...args: unknown[]) => void;

function twq(): Twq | null {
  if (typeof window === "undefined") return null;
  const fn = (window as unknown as { twq?: Twq }).twq;
  return typeof fn === "function" ? fn : null;
}

/**
 * Fire an X conversion. `conversionId` is an optional dedup key (e.g. a user id) so
 * X collapses repeat fires of the same conversion. Never pass an email address.
 */
export function xConvert(eventId: string, conversionId?: string) {
  try {
    const t = twq();
    if (!t) return; // pixel not configured, or uwt.js hasn't loaded yet
    t("event", eventId, {
      conversion_id: conversionId ?? null,
      email_address: null, // never send PII — see the privacy note above
    });
  } catch {
    /* ad tracking must never throw into a user flow */
  }
}
